import type { ApiResponse } from '@/app/common/types/common';

export type AnalysisId = string;
export type ReportLanguage = 'fa' | 'en';

export type AnalysisDeviceType = 'mobile' | 'desktop' | string;
export type AnalysisInternetType = string;
export type AnalysisLocationCode = string;

export type AnalysisHeaderPair = {
  name?: string;
  value?: string;
};

export type AnalysisCookie = {
  name?: string;
  value?: string;
  domain?: string;
  path?: string;
  secure?: boolean;
  httpOnly?: boolean;
  [key: string]: unknown;
};

export type AnalysisSettings = {
  url?: string;
  location_code?: AnalysisLocationCode;
  device_type?: AnalysisDeviceType;
  internet_type?: AnalysisInternetType;
  user_agent?: string;
  headers?: AnalysisHeaderPair[];
  cookies?: AnalysisCookie[];
  is_public?: boolean;
  slug?: string | null;
  [key: string]: unknown;
};

export type AnalysisHistoryItem = {
  _id: string;
  task_id?: string;
  url?: string;
  is_public?: boolean;
  slug?: string | null;
  status?: number;
  is_scheduled?: boolean;
  location_code?: AnalysisLocationCode;
  device_type?: AnalysisDeviceType;
  internet_type?: AnalysisInternetType;
  user_agent?: string;
  headers?: AnalysisHeaderPair[];
  cookies?: AnalysisCookie[];
  site_health?: number;
  full_loading_time?: number | string;
  performance?: number;
  accessibility?: number;
  best_practice?: number;
  seo?: number;
  created_at?: string | number;
  [key: string]: unknown;
};

export type AnalysisHistoryResponse = ApiResponse<AnalysisHistoryItem[]>;

export type AnalysisServerStatus = 'active' | 'inactive' | string;

export type AnalysisServerRow = {
  location_code: string;
  status: AnalysisServerStatus;
  description: string | null;
};

export type AnalysisServersResponse = ApiResponse<{
  rows: AnalysisServerRow[];
}>;

export type CreateAnalysisBody = {
  url: string[];
  location_code: AnalysisLocationCode;
  device_type: AnalysisDeviceType;
  internet_type?: AnalysisInternetType;
  user_agent?: string;
  headers?: AnalysisHeaderPair[];
  cookies?: AnalysisCookie[];
};

export type CreateAnalysisRequest = {
  body: CreateAnalysisBody;
  country?: AnalysisLocationCode;
};

export type CreateAnalysisResponseData = {
  /** New lighthouse API: Bull/history UUID */
  job_id?: string;
  /** New lighthouse API: history UUIDs (one per URL) */
  task_id?: string[];
  /** Legacy sync API */
  history_id?: string;
} & Record<string, unknown>;

export type CreateAnalysisResponse = {
  success?: boolean;
  code?: number;
  data?: CreateAnalysisResponseData | string;
  msg?: string;
};

export type CompareStrategy = 'mobile' | 'desktop' | string;

export type CompareRequest = {
  mode: 'pages';
  task_ids: string[];
  strategy: CompareStrategy;
};

export type ComparisonMetric = {
  difference?: number;
  winner?: 'page-a' | 'page-b' | 'tie' | string;
};

export type ComparisonPair = {
  from?: {
    task_id?: string;
    url?: string;
  };
  to?: {
    task_id?: string;
    url?: string;
  };
  performance?: ComparisonMetric;
  seo?: ComparisonMetric;
  accessibility?: ComparisonMetric;
  best_practice?: ComparisonMetric;
  site_health?: ComparisonMetric;
  lcp?: ComparisonMetric;
  cls?: ComparisonMetric;
  tbt?: ComparisonMetric;
  fcp?: ComparisonMetric;
  si?: ComparisonMetric;
  tti?: ComparisonMetric;
};

export type ComparisonPage = {
  task_id?: string;
  url?: string;
  finalUrl?: string;
  status?: string;
  created_at?: string | number;
  location_code?: string;
  internet_type?: string;
  device_type?: string;
  server?: string;
  performance?: number;
  seo?: number;
  accessibility?: number;
  best_practice?: number;
  site_health?: number;
  lcp?: number;
  cls?: number;
  tbt?: number;
  fcp?: number;
  si?: number;
  tti?: number;
  ratings?: Record<string, string>;
};

export type ComparisonResponseData = {
  strategy?: CompareStrategy;
  pages?: ComparisonPage[];
  comparison?: ComparisonPair[];
};

export type ComparisonResponse = ApiResponse<ComparisonResponseData>;

export type ActiveAnalysisJob = {
  jobId: string;
  url?: string;
};

/** Prefer task_id[0], then job_id, then legacy history_id */
export function getAnalysisTaskIdFromCreateResponse(
  res: CreateAnalysisResponse | undefined | null
): string | null {
  const data = res?.data;
  if (!data || typeof data === 'string') return null;
  if (Array.isArray(data.task_id) && data.task_id[0]) {
    return String(data.task_id[0]);
  }
  if (typeof data.job_id === 'string' && data.job_id) return data.job_id;
  if (typeof data.history_id === 'string' && data.history_id) {
    return data.history_id;
  }
  return null;
}

export type AnalysisRobots = {
  robotsFileUrl?: string;
  robotsTag?: string;
};

export type AnalysisContentInner = {
  estimated_time?: string | number;
  word_count?: string | number;
  sentences_count?: string | number;
  word_density_array_1?: Record<string, unknown>[];
  word_density_array_2?: Record<string, unknown>[];
  word_density_array_3?: Record<string, unknown>[];
  word_density_array_4?: Record<string, unknown>[];
  word_density_array_5?: Record<string, unknown>[];
  [key: string]: unknown;
};

export type AnalysisPageContent = {
  title?: string;
  description?: string;
  url?: string;
  canonical?: string;
  robots?: AnalysisRobots;
  langauge?: string;
  publisher?: string;
  keywords?: string;
  content?: AnalysisContentInner;
  headers?: Record<string, unknown>[];
  links?: Record<string, unknown>[];
  images?: Record<string, unknown>[];
  metas?: Record<string, unknown>[];
  [key: string]: unknown;
};

export type LinkStats = {
  count_links?: Record<string, number> & {
    more?: number;
    '1'?: number;
    '2'?: number;
    '3'?: number;
    '4'?: number;
    '5'?: number;
  };
  protocol?: {
    https?: number;
    http?: number;
    total?: number;
  };
  rel?: {
    follow?: number;
    nofollow?: number;
    total?: number;
  };
  mode?: {
    unique?: number;
    internal?: number;
    external?: number;
  };
  [key: string]: unknown;
};

export type HeaderStats = Record<string, number>;

export type ImageStats = {
  alt?: {
    total?: number;
    with_alt?: number;
    without_alt?: number;
  };
  title?: {
    with_title?: number;
    without_title?: number;
  };
  [key: string]: unknown;
};

export type MetaStats = Record<string, number>;

export type DensityStats = {
  word_density_array_1?: number;
  word_density_array_2?: number;
  word_density_array_3?: number;
  word_density_array_4?: number;
  word_density_array_5?: number;
  [key: string]: unknown;
};

export type AnalysisStatistics = {
  link_stats?: LinkStats;
  header_stats?: HeaderStats;
  image_stats?: ImageStats;
  meta_stats?: MetaStats;
  density_stats?: DensityStats;
  [key: string]: unknown;
};

/** Lighthouse category score payload used by CircularPieCharts */
export type LighthouseCategory = {
  id?: string;
  title?: string;
  description?: string;
  score?: number | null;
  manualDescription?: string;
  auditRefs?: LighthouseAuditRef[];
  name?: string;
  [key: string]: unknown;
};

export type LighthouseAuditRef = {
  id: string;
  weight?: number;
  group?: string;
  [key: string]: unknown;
};

/** Deeply nested / dynamic lighthouse audit objects — use sparingly */
export type LighthouseAudit = {
  id?: string;
  title?: string;
  description?: string;
  score?: number | null;
  scoreDisplayMode?: string;
  displayValue?: string;
  explanation?: string;
  errorMessage?: string;
  numericValue?: number;
  numericUnit?: string;
  details?: any;
  [key: string]: any;
};

export type LighthouseThrottling = {
  cpuSlowdownMultiplier?: number;
  rttMs?: number;
  throughputKbps?: number;
  [key: string]: unknown;
};

export type LighthouseConfigSettings = {
  throttling?: LighthouseThrottling;
  throttlingMethod?: string;
  emulatedFormFactor?: string;
  [key: string]: unknown;
};

export type LighthouseDetails = {
  requestedUrl?: string;
  fetchTime?: string;
  lighthouseVersion?: string;
  userAgent?: string;
  categories?: Record<string, LighthouseCategory>;
  audits?: Record<string, LighthouseAudit>;
  categoryGroups?: Record<
    string,
    { title?: string; description?: string; [key: string]: unknown }
  >;
  environment?: {
    benchmarkIndex?: number;
    [key: string]: unknown;
  };
  configSettings?: LighthouseConfigSettings;
  i18n?: {
    rendererFormattedStrings?: {
      varianceDisclaimer?: string;
      notApplicableAuditsGroupTitle?: string;
      manualAuditsGroupTitle?: string;
      passedAuditsGroupTitle?: string;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  stackPacks?: Array<{
    title?: string;
    iconDataURL?: string;
    descriptions?: Record<string, string>;
    [key: string]: unknown;
  }>;
  [key: string]: any;
};

export type AnalysisTaskStatusPayload = {
  task_id?: string;
  status?: number;
};

export type AnalysisOverviewData = AnalysisTaskStatusPayload & {
  settings?: AnalysisSettings;
  content?: AnalysisPageContent;
  statistics?: AnalysisStatistics;
  details?: LighthouseDetails;
  [key: string]: unknown;
};

export type AnalysisOverviewResponse = ApiResponse<AnalysisOverviewData | null>;

export type AnalysisContentData = AnalysisTaskStatusPayload & {
  settings?: AnalysisSettings;
  content?: AnalysisPageContent;
  statistics?: AnalysisStatistics;
  [key: string]: unknown;
};

export type AnalysisContentResponse = ApiResponse<AnalysisContentData | null>;

export type AnalysisSpeedReport = {
  strategy?: string;
  created_at?: string;
  statistics?: AnalysisStatistics;
  details?: LighthouseDetails;
  [key: string]: unknown;
};

export type AnalysisSpeedData = AnalysisTaskStatusPayload & {
  settings?: AnalysisSettings;
  details?: LighthouseDetails | LighthouseDetails[];
  reports?: AnalysisSpeedReport[];
  created_at?: string;
  strategy?: 'mobile' | 'desktop' | string;
  device_type?: 'both' | 'mobile' | 'desktop' | string;
  available_strategies?: string[];
  [key: string]: unknown;
};

export type AnalysisSpeedResponse = ApiResponse<AnalysisSpeedData | null>;

export type AnalysisPdfSection =
  | 'performance'
  | 'accessibility'
  | 'seo'
  | 'best-practices';

export type AnalysisPdfDownloadRequest = {
  task_id: AnalysisId;
  sections: AnalysisPdfSection[] | null;
  report_lang: ReportLanguage;
};

export type CruxRating = 'good' | 'needs-improvement' | 'poor' | string;

export type CruxHistogramBin = {
  start?: number;
  end?: number;
  density?: number;
};

export type CruxMetric = {
  p75?: number;
  rating?: CruxRating;
  histogram?: CruxHistogramBin[];
};

export type CruxFieldExperience = {
  available?: boolean;
  form_factor?: string;
  metrics?: Record<string, CruxMetric>;
};

export type CruxFieldData = {
  page?: CruxFieldExperience | null;
  origin?: CruxFieldExperience | null;
};

export type CruxHistoryMetric = {
  histogramTimeseries?: Array<{
    start?: number | string;
    end?: number | string;
    densities?: Array<number | string | null>;
  }>;
  percentilesTimeseries?: {
    p75s?: Array<number | string | null>;
  };
};

export type CruxCollectionPeriod = {
  firstDate?: {
    day?: number;
    month?: number;
    year?: number;
  };
  lastDate?: {
    day?: number;
    month?: number;
    year?: number;
  };
};

export type CruxFieldDataHistory = {
  record?: {
    key?: {
      url?: string;
      formFactor?: string;
    };
    metrics?: Record<string, CruxHistoryMetric>;
    collectionPeriods?: CruxCollectionPeriod[];
  };
};

export type CruxReportData = AnalysisTaskStatusPayload & {
  strategy?: string;
  field_data?: CruxFieldData | null;
  field_data_history?: CruxFieldDataHistory | null;
};

export type CruxReportResponse = ApiResponse<CruxReportData | null>;

export type AnalysisMutationResponse =
  | ApiResponse<unknown>
  | { success?: boolean; code?: number; data?: unknown };

export type AnalysisScheduleUrl = {
  url: string;
  device_type: string;
  internet_type: string;
  location_code: string;
};

export type AnalysisSchedule = {
  id: string;
  user_id?: string;
  urls: AnalysisScheduleUrl[];
  notification_method: string | string[];
  scheduled_day: string;
  timezone?: string;
  enabled: boolean;
  created_at?: string;
};

export type AnalysisScheduleInput = {
  urls: AnalysisScheduleUrl[];
  notification_method: string | string[];
  scheduled_day: string;
  enabled: boolean;
};

export type AnalysisScheduleResponse = ApiResponse<
  AnalysisSchedule | AnalysisSchedule[] | { rows: AnalysisSchedule[] }
>;
